import type { ScheduleEntryKind } from './contract'

/**
 * Révision de la facturation d'un Contrat signé (13 oct. 2026) : le prix et les versements restent
 * modifiables tant que toutes les factures ne sont pas payées. Les factures payées restent, les
 * impayées sont annulées et remplacées pour le solde restant (`revise_contract_billing_v1`).
 */
export const contractBillingBlockers = [
  'CONTRACT_NOT_FOUND',
  'NO_INVOICING',
  'NOT_SIGNED',
  'STATUS_LOCKED',
  'LEGACY_TAX_MODE',
  'NO_INVOICES',
  'FULLY_PAID',
  'PARTIAL_PAYMENT',
  'CHECKOUT_IN_PROGRESS',
] as const
export type ContractBillingBlocker = (typeof contractBillingBlockers)[number]

export interface ContractBillingStateEntry {
  id: string
  order: number
  description: string
  amountCents: number
  dueOn?: string
  invoiceId?: string
  invoiceNumber?: string
  paid: boolean
}

export interface ContractBillingState {
  contractId: string
  priceCents: number
  priceTaxes: 'avant_taxes' | 'apres_taxes' | 'sans_taxes'
  totalCents: number
  paidCents: number
  remainingCents: number
  /** Absent : la facturation est révisable. */
  blocker?: ContractBillingBlocker
  entries: ContractBillingStateEntry[]
}

export interface ReviseContractBillingCommand {
  idempotencyKey: string
  contractId: string
  /** Prix avant ou après taxes, selon le régime du Contrat. Absent : prix inchangé. */
  priceCents?: number
  priceOverride?: boolean
  reason: string
  entries: { description: string; kind: ScheduleEntryKind; value: number; dueOn?: string }[]
}

export interface ReviseContractBillingResult {
  remainingCents: number
  cancelledInvoices: number
  createdInvoices: string[]
}

export const contractBillingBlockerLabel: Record<ContractBillingBlocker, string> = {
  CONTRACT_NOT_FOUND: 'Contrat introuvable.',
  NO_INVOICING: 'Ce contrat est sans facturation.',
  NOT_SIGNED: 'Le contrat n’est pas encore signé : l’échéancier se modifie directement.',
  STATUS_LOCKED: 'Le statut du contrat ne permet plus de modifier la facturation.',
  LEGACY_TAX_MODE: 'Ce contrat est facturé « taxes ajoutées » : sa facturation ne se révise pas ici.',
  NO_INVOICES: 'Aucune facture n’a encore été générée pour ce contrat.',
  FULLY_PAID: 'Toutes les factures sont payées : le prix et les versements sont verrouillés.',
  PARTIAL_PAYMENT:
    'Une facture est partiellement payée ou créditée. Réglez-la ou annulez le paiement avant de réviser.',
  CHECKOUT_IN_PROGRESS:
    'Un paiement en ligne est en cours sur une facture impayée. Réessayez dans 24 heures ou après le paiement.',
}

/** Message lisible pour une erreur du serveur à la révision. */
export function contractBillingErrorLabel(message: string): string {
  const blocked = /BILLING_REVISION_BLOCKED:([A-Z_]+)/.exec(message)?.[1]
  const blocker = contractBillingBlockers.find((candidate) => candidate === blocked)
  if (blocker) return contractBillingBlockerLabel[blocker]
  if (message.includes('CONTRACT_PRICE_OVERRIDE_FORBIDDEN'))
    return 'Vous n’avez pas la permission de modifier le prix d’un contrat.'
  if (message.includes('BILLING_REVISION_TOTAL_MISMATCH'))
    return 'La somme des versements doit faire le solde restant.'
  if (message.includes('BILLING_REVISION_TOTAL_NOT_ABOVE_PAID'))
    return 'Le nouveau prix ne dépasse pas ce qui est déjà payé.'
  if (message.includes('BILLING_REVISION_REASON_REQUIRED')) return 'Indiquez la raison du changement.'
  return 'La facturation n’a pas pu être révisée. Rien n’a été modifié.'
}
